import React, { useState, useRef } from 'react';
import {
  CONFIG_FIELDS,
  getConfig,
  saveConfig,
  exportConfigString,
  parseConfigString,
} from './appConfig';

/**
 * 接続設定の入力画面。
 * 初回は単独で表示し、あとから設定モーダル内でも開ける。
 */
function SetupScreen({ onCancel }) {
  const [values, setValues] = useState(() => {
    const cur = getConfig() || {};
    const init = {};
    CONFIG_FIELDS.forEach((f) => {
      init[f.key] = cur[f.key] || '';
    });
    return init;
  });
  const [error, setError] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [pasteNote, setPasteNote] = useState('');
  const [copyNote, setCopyNote] = useState('');
  const pasteRef = useRef(null);

  const origin = window.location.origin;
  const hasExisting = !!getConfig();

  const set = (key, v) => setValues((prev) => ({ ...prev, [key]: v }));

  /**
   * 貼り付けた文字を整える。
   *
   * iPhoneなどでは、コピーの途中で引用符が “ ” に変わってしまい、
   * そのままでは読み取れない。全角の記号や余分な空白も直しておく。
   */
  const cleanse = (raw) =>
    String(raw || '')
      // かぎ付きの引用符を、ふつうの引用符に戻す
      .replace(/[\u201c\u201d\u201e\u2033\uff02]/g, '"')
      .replace(/[\u2018\u2019\u201a\u2032\uff07]/g, "'")
      // 全角の記号を半角に
      .replace(/\uff1a/g, ':')
      .replace(/\uff0c/g, ',')
      .replace(/\uff5b/g, '{')
      .replace(/\uff5d/g, '}')
      .replace(/\u3000/g, ' ')
      // 改行コードをそろえ、見えない文字を落とす
      .replace(/\r\n?/g, '\n')
      .replace(/\u200b/g, '')
      .replace(/\u200c/g, '')
      .replace(/\u200d/g, '')
      .replace(/\ufeff/g, '')
      .trim();

  /**
   * 「設定をコピー」で作った1行の文字列を整える。
   *
   * ・途中に改行や空白が入ることがあるので取り除く
   * ・目印の MTLCFG1: が小文字になっていることがあるので大文字に直す
   *   （iPhoneでは、貼り付けたあとに勝手に小文字へ変わることがある）
   *
   * 目印より後ろは中身そのものなので、大文字小文字は変えない。
   */
  const joinOneLine = (text) => {
    if (!/^\s*mtlcfg\d*\s*:/i.test(text)) return text;
    const packed = text.replace(/\s+/g, '');
    return packed.replace(/^mtlcfg(\d*):/i, (m, digits) => `MTLCFG${digits || '1'}:`);
  };

  /**
   * 「apiKey: "xxx"」のような並びから、1項目ずつ拾う。
   * まとめて読めなかったときの受け皿。
   */
  const pickByKey = (text) => {
    const out = {};
    CONFIG_FIELDS.forEach((f) => {
      const re = new RegExp(f.key + '\\s*[:=]\\s*["\']?([^"\',}\\n]+)', 'i');
      const m = text.match(re);
      if (m && m[1]) out[f.key] = m[1].trim().replace(/[,;]$/, '');
    });
    return out;
  };

  const handlePaste = () => {
    // 入力中の内容を取りこぼさないよう、欄から直接読む
    const raw = pasteRef.current ? pasteRef.current.value : pasteText;
    const text = joinOneLine(cleanse(raw));

    if (!text) {
      setPasteNote('');
      setError('貼り付けられていません');
      return;
    }

    // まず今までの方法で読む。だめなら1項目ずつ拾う
    let parsed = {};
    const res = parseConfigString(text);
    if (!res.error && res.values) parsed = res.values;

    const picked = pickByKey(text);
    CONFIG_FIELDS.forEach((f) => {
      if (!parsed[f.key] && picked[f.key]) parsed[f.key] = picked[f.key];
    });

    const found = CONFIG_FIELDS.filter((f) => parsed[f.key]);
    if (found.length === 0) {
      setPasteNote('');
      setError(
        `読み取れませんでした（${text.length}文字 / ${text.slice(0, 12)}...）。` +
          '下の欄に直接入力してください'
      );
      return;
    }

    setValues((prev) => {
      const next = { ...prev };
      found.forEach((f) => {
        next[f.key] = parsed[f.key];
      });
      return next;
    });

    const missing = CONFIG_FIELDS.filter((f) => !parsed[f.key] && !(values[f.key] || '').trim());
    setError('');
    setPasteNote(
      missing.length > 0
        ? `${found.length}項目を読み取りました（${missing
            .map((f) => f.label)
            .join('、')} は手で入力してください）`
        : `${found.length}項目を読み取りました`
    );
    setPasteText('');
  };

  const handleCopy = async () => {
    const str = exportConfigString(values);
    if (!str) return;
    try {
      await navigator.clipboard.writeText(str);
      setCopyNote('コピーしました');
    } catch {
      // クリップボードが使えない場合は選択できる形で出す
      window.prompt('この文字列をコピーしてください', str);
      setCopyNote('');
    }
    setTimeout(() => setCopyNote(''), 3000);
  };

  const handleSave = () => {
    const missing = CONFIG_FIELDS.filter((f) => !(values[f.key] || '').trim());
    if (missing.length > 0) {
      setError(`未入力の項目があります：${missing.map((f) => f.label).join('、')}`);
      return;
    }
    saveConfig(values);
    window.location.reload();
  };

  const firebaseFields = CONFIG_FIELDS.filter((f) => f.group === 'firebase');
  const googleFields = CONFIG_FIELDS.filter((f) => f.group === 'google');

  return (
    <div className="setup-screen">
      <div className="setup-card">
        <h2>接続設定</h2>

        <p className="setup-lead">
          このアプリは、あなた自身のFirebaseとGoogle Cloudに接続して動きます。
          以下の情報をすべて入力してください。
        </p>

        {/* ===== まとめて貼り付け ===== */}
        <section className="setup-section paste-box">
          <h3>まとめて貼り付け</h3>
          <p className="setup-desc">
            他の端末で「設定をコピー」した文字列を貼ると、下の項目が埋まります。
            Firebaseコンソールに表示される firebaseConfig をそのまま貼っても読み取れます
            （その場合、OAuth Client ID だけ手で入力してください）。
          </p>
          <textarea
            ref={pasteRef}
            className="paste-input"
            rows={3}
            value={pasteText}
            placeholder="ここに貼り付け"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            onChange={(e) => setPasteText(e.target.value)}
          />
          <div className="paste-actions">
            <button className="gcal-btn small" onClick={handlePaste}>
              読み取る
            </button>
            {pasteNote && <span className="gcal-note">{pasteNote}</span>}
          </div>
        </section>

        {/* ===== Firebase ===== */}
        <section className="setup-section">
          <h3>Firebase設定（6項目）</h3>
          <p className="setup-desc">他の端末との同期に使います。</p>
          <p className="setup-where">
            取得場所：Firebaseコンソール → プロジェクトの設定 → 全般 →
            マイアプリ → SDKの設定と構成 →「構成」
          </p>

          {firebaseFields.map((f) => (
            <label key={f.key} className="setup-field">
              <span>{f.label}</span>
              <input
                type="text"
                value={values[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                autoComplete="off"
                spellCheck="false"
              />
            </label>
          ))}
        </section>

        {/* ===== Google Cloud ===== */}
        <section className="setup-section">
          <h3>OAuth クライアントID（1項目）</h3>
          <p className="setup-desc">Googleカレンダーとの連携に使います。</p>
          <p className="setup-where">
            取得場所：Google Cloud Console → APIとサービス → 認証情報 →
            OAuth 2.0 クライアントID
          </p>

          {googleFields.map((f) => (
            <label key={f.key} className="setup-field">
              <span>{f.label}</span>
              <input
                type="text"
                value={values[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                autoComplete="off"
                spellCheck="false"
              />
            </label>
          ))}
        </section>

        {error && <div className="setup-error">{error}</div>}

        <div className="setup-actions">
          <button className="setup-save" onClick={handleSave}>
            保存して開始
          </button>
          {hasExisting && (
            <button className="setup-copy" onClick={handleCopy}>
              設定をコピー
            </button>
          )}
          {onCancel && (
            <button className="setup-cancel" onClick={onCancel}>
              キャンセル
            </button>
          )}
          {copyNote && <span className="gcal-note">{copyNote}</span>}
        </div>

        {hasExisting && (
          <p className="setup-desc" style={{ marginTop: -12, marginBottom: 24 }}>
            「設定をコピー」を押すと、7項目をまとめた1行の文字列がコピーされます。
            メモアプリなどで別の端末に送り、上の欄に貼り付けてください。
          </p>
        )}

        {/* ===== 事前に必要な作業 ===== */}
        <section className="setup-section note">
          <h3>入力の前に、以下を済ませてください</h3>

          <ol className="setup-steps">
            <li>
              <b>Google Cloud Console</b> で Google Calendar API を有効化
            </li>
            <li>
              <b>OAuth同意画面</b> を作成（外部）し、スコープに次の2つを追加
              <div className="setup-code">
                https://www.googleapis.com/auth/calendar.readonly
                <br />
                https://www.googleapis.com/auth/calendar.events
              </div>
              テストユーザーに自分のGmailアドレスを追加
            </li>
            <li>
              <b>OAuthクライアントID</b>（ウェブアプリケーション）を作成し、
              「承認済みのJavaScript生成元」に次を登録
              <div className="setup-code">{origin}</div>
            </li>
            <li>
              <b>Firebase</b> でプロジェクトを作成し、Authentication の
              Googleログインを有効化。「承認済みドメイン」に次を追加
              <div className="setup-code">{window.location.hostname}</div>
            </li>
            <li>
              <b>Firestore Database</b> を作成（本番環境モード / asia-northeast1）し、
              ルールを次に置き換えて公開
              <div className="setup-code">
                {`rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    match /users/{userId}/{document=**} {
      allow read, write: if request.auth != null
        && request.auth.uid == userId;
    }
  }
}`}
              </div>
            </li>
            <li>
              <b>Firebase</b> のウェブアプリを登録し、表示される firebaseConfig の
              6項目を上のフォームに入力
            </li>
          </ol>

          <p className="setup-desc">
            入力した情報はこのブラウザの中だけに保存されます。
            ブラウザのデータを消すと再入力が必要になるので、控えを残しておいてください。
          </p>
        </section>
      </div>
    </div>
  );
}

export default SetupScreen;
